'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { InstallmentCalculatorCallToActionFragmentFragmentDoc } from '@packages/gql/generated/shopping/InstallmentCalculatorCallToActionFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { InstallmentCalculatorInCard } from './InstallmentCalculatorInCard/InstallmentCalculatorInCard.client';
import { InstallmentCalculatorInDialog } from './InstallmentCalculatorInDialog/InstallmentCalculatorInDialog.client';

/* GraphQL */ `
  fragment InstallmentCalculatorCallToActionFragment on InstallmentData {
    numberOfInstallments
    singleInstallment {
      value
      format
    }
    currency
  }
`;

export interface InstallmentCalculatorCallToActionProps {
  /**
   * PromotionFragment data fragment
   */
  installmentData: FragmentType<typeof InstallmentCalculatorCallToActionFragmentFragmentDoc>;
  /**
   * Product price value as float (e.g. 13.37)
   */
  basePrice: number;
}

export const InstallmentCalculatorCallToAction = ({
  installmentData,
  basePrice,
}: InstallmentCalculatorCallToActionProps) => {
  const {
    installmentCalculator: { InstallmentCalculatorCallToActionLayout },
  } = useConfig();

  const { numberOfInstallments, singleInstallment, currency } =
    unmask<typeof InstallmentCalculatorCallToActionFragmentFragmentDoc>(installmentData);

  if (numberOfInstallments === 0 || !singleInstallment?.value) {
    return null;
  }

  if (
    InstallmentCalculatorCallToActionLayout &&
    InstallmentCalculatorCallToActionLayout.layout === 'IconInstallmentsAndLegalHints' &&
    InstallmentCalculatorCallToActionLayout.openCalculatorIn.type === 'Card'
  ) {
    return (
      <InstallmentCalculatorInCard
        singleInstallment={singleInstallment.value}
        currency={currency}
        calculatorID={InstallmentCalculatorCallToActionLayout.openCalculatorIn.calculatorID}
      />
    );
  }

  return (
    <InstallmentCalculatorInDialog
      singleInstallment={singleInstallment.value}
      currency={currency}
      numberOfInstallments={numberOfInstallments}
      basePrice={basePrice}
    />
  );
};
